import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CompensationHistoryCard } from "@/components/employees/CompensationHistoryCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Building, Calendar, DollarSign, MapPin, FileText } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { format, differenceInYears } from "date-fns";
import { parseDateLocal } from "@/lib/utils";

export default function Employment() {
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);

  // Fetch current employee data
  const { data: employee, isLoading } = useQuery({
    queryKey: ["/api/employees/me"],
    queryFn: async () => {
      const response = await fetch("/api/employees/me");
      if (!response.ok) {
        throw new Error("Failed to fetch employee data");
      }
      return response.json();
    },
  });

  const handleRequestUpdate = () => {
    setIsRequestDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsRequestDialogOpen(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading employment information...</div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">Failed to load employment information</div>
      </div>
    );
  }

  // Calculate years of service. Use parseDateLocal so a YYYY-MM-DD value
  // stored as a calendar date isn't shifted by the browser's timezone.
  const hireDateLocal = parseDateLocal(employee.hireDate);
  const yearsOfService = hireDateLocal
    ? differenceInYears(new Date(), hireDateLocal)
    : 0;

  // Format employment type
  const formatEmploymentType = (type: string | null) => {
    if (!type) return "Not specified";
    return type.replace(/[-_]/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase());
  };

  // Format salary display
  const formatSalary = (amount: string | null, type: string | null) => {
    if (!amount) return "Not specified";
    const numericAmount = parseFloat(amount.replace(/[^0-9.]/g, ""));
    if (isNaN(numericAmount)) return amount;
    
    const formatted = `$${numericAmount.toLocaleString()}`;
    if (type?.toLowerCase() === "salary") {
      return `${formatted} / year`;
    } else if (type?.toLowerCase() === "hourly") {
      return `${formatted} / hour`;
    }
    return formatted;
  };

  // Format pay cycle
  const formatPayCycle = (cycle: string | null) => {
    if (!cycle) return "Not specified";
    return cycle.replace(/[-_]/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase());
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Employment Information</h1>
        <Button variant="outline" size="sm" onClick={handleRequestUpdate}>
          <FileText className="h-4 w-4 mr-2" />
          Request Update
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              Position Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-sm font-medium">Job Title</span>
              <span className="text-sm">{employee.position || "Not specified"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium">Department</span>
              <span className="text-sm">{employee.department || "Not specified"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium">Employee ID</span>
              <span className="text-sm">{employee.employeeId || "Not assigned"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium">Employment Type</span>
              <Badge variant="default">{formatEmploymentType(employee.employmentType)}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium">Scheduled Hours</span>
              <span className="text-sm">{employee.scheduledHours ? `${employee.scheduledHours} hours/week` : "Not specified"}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Employment History
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-sm font-medium">Start Date</span>
              <span className="text-sm">
                {hireDateLocal
                  ? format(hireDateLocal, "MMMM d, yyyy")
                  : "Not specified"
                }
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium">Years of Service</span>
              <span className="text-sm">
                {yearsOfService > 0 
                  ? `${yearsOfService} year${yearsOfService === 1 ? "" : "s"}`
                  : "Less than 1 year"
                }
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium">Last Review</span>
              <span className="text-sm text-muted-foreground italic">Coming soon</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium">Performance Rating</span>
              <span className="text-sm text-muted-foreground italic">Coming soon</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium">Status</span>
              <Badge 
                variant={employee.status?.toLowerCase() === "active" ? "default" : "secondary"}
              >
                {employee.status?.charAt(0).toUpperCase() + employee.status?.slice(1) || "Unknown"}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Compensation Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-sm font-medium">
                  {employee.compensationType?.toLowerCase() === "salary" ? "Annual Salary" : "Hourly Rate"}
                </span>
                <span className="text-sm">
                  {formatSalary(employee.compensationAmount || employee.salary, employee.compensationType)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Pay Frequency</span>
                <span className="text-sm">{formatPayCycle(employee.payCycle)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Compensation Type</span>
                <span className="text-sm">
                  {employee.compensationType?.charAt(0).toUpperCase() + employee.compensationType?.slice(1) || "Not specified"}
                </span>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-sm font-medium">Address</span>
                <span className="text-sm text-right">
                  {employee.address1 || employee.city || employee.state 
                    ? [employee.address1, employee.city, employee.state].filter(Boolean).join(", ")
                    : "Not provided"
                  }
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Phone</span>
                <span className="text-sm">{employee.phone || "Not provided"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Email</span>
                <span className="text-sm">{employee.email}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Read-only pay history + W-2 entries (employees cannot edit their own compensation) */}
      <CompensationHistoryCard employeeId={employee.id} canEdit={false} compact />

      {/* Request Update Dialog */}
      <Dialog open={isRequestDialogOpen} onOpenChange={setIsRequestDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Update Request Sent
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-center text-muted-foreground">
              Your employer has been notified of your update request.
            </p>
          </div>
          <div className="flex justify-center">
            <Button onClick={handleDialogClose}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}